// A part's projects as chips (Manufacturing projects): solid for the projects whose builds use it, dashed "GCU2 · draft"
// for one where only a draft or scheduled revision does (which one in the tooltip). A part is in the projects whose BOMs
// use it, never one of its own; the Parts library, the item panel and the BOM editor show them the same way.
import { ownerLabel, revisionState } from "../../../../shared/boms.ts";
import { type ProjectIndex, partProjects, projectPath, reachOf } from "../../../../shared/projects.ts";
import { Link } from "../../lib/router.tsx";
import { cx } from "../../ui/controls.tsx";

/** "Only GC Ultimate 2 Rev 2 (draft) uses it": the revisions that bring a part into a project before they're in use. */
export function draftNote(px: ProjectIndex, projectId: string, partId: string): string {
  const ix = px.ix;
  const uses = reachOf(px).byProject.get(projectId)?.onDraft.get(partId) ?? [];
  const boms = [...new Set(uses.map((u) => u.bomId))].flatMap((id) => ix.bomById.get(id) ?? []);
  const pending = boms.filter((b) => b.status === "draft" || revisionState(ix, b) === "scheduled");
  const name = px.projectById.get(projectId)?.name ?? "the project";
  if (!pending.length) return `Only a draft of ${name} uses it, through ${boms.map((b) => ownerLabel(ix, b)).join(", ")}`;
  return `Only ${pending.map((b) => `${ownerLabel(ix, b)} Rev ${b.revision} (${revisionState(ix, b)})`).join(", ")} uses it`;
}

/** One project as a chip; `link`: it opens the project. */
export function ProjectChip({ px, id, draftOf, link = true }: { px: ProjectIndex; id: string; draftOf?: string; link?: boolean }) {
  const p = px.projectById.get(id);
  if (!p) return null;
  const text = `${p.code || p.name}${draftOf ? " · draft" : ""}`;
  const title = draftOf ? draftNote(px, id, draftOf) : p.name;
  const className = cx(
    "inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[11px] text-accent-text",
    draftOf ? "border border-dashed border-accent/40" : "bg-accent-soft font-medium",
    link && "hover:underline",
  );
  return link ? (
    <Link to={projectPath(p)} onClick={(e) => e.stopPropagation()} title={title} className={className}>
      {text}
    </Link>
  ) : (
    <span title={title} className={className}>
      {text}
    </span>
  );
}

/** A part's projects: in use first, then those only a draft brings it into. Nothing when it's in none. */
export function PartProjectChips({ px, id, link = true }: { px: ProjectIndex; id: string; link?: boolean }) {
  const pp = partProjects(px).get(id);
  if (!pp || (!pp.inUse.length && !pp.onDraft.length)) return null;
  return (
    <>
      {pp.inUse.map((p) => (
        <ProjectChip key={p} px={px} id={p} link={link} />
      ))}
      {pp.onDraft.map((p) => (
        <ProjectChip key={p} px={px} id={p} draftOf={id} link={link} />
      ))}
    </>
  );
}

/** For Copy and CSV: "GC Ultimate 2; ProGCC 3S (draft)". */
export function partProjectsText(px: ProjectIndex, id: string): string {
  const pp = partProjects(px).get(id);
  const name = (p: string) => px.projectById.get(p)?.name ?? "";
  return [...(pp?.inUse ?? []).map(name), ...(pp?.onDraft ?? []).map((p) => `${name(p)} (draft)`)].join("; ");
}
